import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import { useScopedSelect } from '@/contexts/ClientScopeContext';

/** Domains module (dmarcian + CIPP Domain Analyser) — mirrors server/domains/. */

export type Stage = 'missing' | 'monitoring' | 'quarantine' | 'reject';

export interface DomainRow {
  domain: string;
  id: number;
  active: boolean;
  isSubdomain: boolean;
  company: { id: number | null; name: string | null; source: 'cipp' | 'parent' | 'manual' | 'ignored' | 'none' };
  stage: Stage;
  policyLabel: string;
  pct: number | null;
  inherited: boolean;
  volume7d: number;
  compliancePct: number | null;
  recommendation: string | null;
  recommendedRecord: string | null;
  dmarcianReady: boolean;
  readyToEnforce: boolean;
  policySince: string | null;
  daysAtPolicy: number | null;
  spf: { label: string | null; ok: boolean | null; lookups: number | null };
  dkim: { label: string | null; ok: boolean | null; selectors: number };
  senders: number;
  failingSenders: number;
  openTasks: number;
  openIssues: number;
  lastReport: string | null;
  cipp: { score: number | null; notes: string[]; dkimEnabled: boolean | null; mxProvider: string | null } | null;
  portalUrl: string;
}

export interface UnmonitoredDomain { domain: string; companyId: number | null; companyName: string | null; tenant: string | null; dmarc: string; score: number | null }

export type Tier = 'E' | 'P' | 'S' | 'H' | 'L';
export type DomainSystem = 'cipp' | 'itglue' | 'dmarcian';

/** IT Glue "Email Security" flexible asset: Synfo's daily automated SPF / DKIM / DMARC check (treated as accurate). */
export interface EmailSecurity {
  id: string; url: string | null; updatedAt: string | null;
  spf: boolean; spfRecord: string | null; dkim: boolean; dkimRecord: string | null; dmarc: boolean; dmarcRecord: string | null;
  policy: 'none' | 'quarantine' | 'reject' | null;
}

/** One client domain across dmarcian + CIPP + IT Glue (mirrors server/domains/data.ts DomainEntry). */
export interface DomainEntry {
  domain: string;
  company: { id: number | null; name: string | null; source: string };
  tier: Tier | null;
  stage: Stage;
  stageSource: DomainSystem | null;
  spf: boolean | null;
  dkim: boolean | null;
  in: Record<DomainSystem, boolean>;
  dmarcianRequired: boolean;
  missing: DomainSystem[];
  m365Mail: boolean | null;
  dmarcian: DomainRow | null;
  itglue: { orgId: string; orgName: string; domainUrl: string | null; registrar: string | null; expiresOn: string | null } | null;
  emailSecurity: EmailSecurity | null;
  cipp: { tenant: string | null; score: number | null } | null;
}

export interface DomainsResponse { items: DomainRow[]; unmonitored: UnmonitoredDomain[]; entries: DomainEntry[]; fetchedAt: string; readyDays: number; readyPct: number }

export interface DomainDetail {
  entry: DomainEntry;
  /** dmarcian's view — null when the domain isn't in dmarcian. */
  domain: DomainRow | null;
  records: { dmarc: string | null; spf: string | null; dmarcMessage: string | null; spfMessage: string | null; dkimMessage: string | null } | null;
  dkimSelectors: { selector: string; lastSeen: string }[];
  impact: { total_emails?: number; impacted_emails?: number; impacted_categories?: string[] } | null;
  readinessSources: { label: string; ready: boolean; messages_compliant: number; messages_total: number; compliance_percentage: string; category: string }[];
  senders: { name: string; messages: number; spfPct: number; dkimPct: number; dmarcPct: number }[];
  tasks: { id: number; category: string; details: string; created: string }[];
  issues: { id: number; problem: string; solution: string | null; created: string }[];
  history: { date: string; type: string; labels: string[]; changes: { label: string; old: string; new: string }[] }[];
  cipp: { score: number | null; notes: string[]; dkimEnabled: boolean | null; spfPass: boolean | null; mxPass: boolean | null; checkedAt: string | null } | null;
}

/** `scoped`: narrow to the client picked in the header (ClientScopeContext). */
export function useDomains(opts: { scoped?: boolean } = {}) {
  const select = useScopedSelect<DomainsResponse>(opts.scoped, (d, inScope) => ({
    ...d, items: d.items.filter((x) => inScope(x.company.id)), unmonitored: d.unmonitored.filter((x) => inScope(x.companyId)), entries: d.entries.filter((x) => inScope(x.company.id)),
  }));
  return useQuery({ select, queryKey: ['domains', 'list'], queryFn: () => api.get<DomainsResponse>('/api/domains'), staleTime: 5 * 60_000 });
}

export function useDomain(domain: string | undefined) {
  return useQuery({ queryKey: ['domains', 'one', domain], queryFn: () => api.get<DomainDetail>(`/api/domains/${encodeURIComponent(domain!)}`), enabled: !!domain, staleTime: 5 * 60_000 });
}

export function useSetDomainMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ domain, value }: { domain: string; value: { companyId: number; companyName: string } | { ignored: true } | null }) =>
      value ? api.put(`/api/domains/${encodeURIComponent(domain)}/map`, value) : api.del(`/api/domains/${encodeURIComponent(domain)}/map`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['domains'] }); qc.invalidateQueries({ queryKey: ['insights'] }); },
  });
}

export const RECOMMENDATION_LABEL: Record<string, string> = {
  publish_policy: 'Tighten policy', monitor: 'Monitor', collect_data: 'Collecting data', improve_compliance: 'Improve compliance',
  lower_policy: 'Lower policy', add_dmarc: 'Add DMARC record',
};
